import { Text } from '@mantine/core';
import { IconTargetArrow } from '@tabler/icons-react';
import cn from 'classnames';
import React from 'react';

import { useDevices } from '@/utils/devices';
import { hexToRgba } from '@/utils/hexToRgba';

import { getSimilarityColor, normalizeScoreToPercent } from './utils';

import s from './ScoreBadge.module.scss';

type ScoreBadgeProps = {
  label: string;
  value: number | null;
  size?: 'sm' | 'md';
  className?: string;
};

const ScoreBadge: React.FC<ScoreBadgeProps> = ({ label, value, size = 'md', className }) => {
  const { isCompactViewport } = useDevices();
  const similarityPercent = React.useMemo(() => normalizeScoreToPercent(value), [value]);

  const similarityColor = React.useMemo(() => {
    if (similarityPercent === null) {
      return null;
    }

    return getSimilarityColor(similarityPercent);
  }, [similarityPercent]);

  const similarityBackground = React.useMemo(() => {
    if (!similarityColor) {
      return null;
    }

    return hexToRgba(similarityColor, 0.1);
  }, [similarityColor]);

  if (similarityPercent === null || !similarityColor || !similarityBackground || value === null) {
    return null;
  }

  return (
    <div className={cn(s.root, s[`root_${size}`], className)} style={{ backgroundColor: similarityBackground }}>
      <IconTargetArrow size={size === 'sm' ? 12 : 14} color={similarityColor} />
      {!isCompactViewport && (
        <Text size={size === 'sm' ? 'xxs' : 'xs'} fw={400} style={{ color: similarityColor }}>
          {label}
        </Text>
      )}
      <Text size={size === 'sm' ? 'xxs' : 'xs'} fw={500} style={{ color: similarityColor }}>
        {`${similarityPercent}%`}
      </Text>
    </div>
  );
};

export default React.memo(ScoreBadge);
